/**
 * O vínculo do paciente do Caso com o cadastro do IW (`TP-053`, `T7`).
 *
 * Os candidatos são buscados no IW a cada abertura e nunca guardados (`D6`): por isso a consulta tem `staleTime: 0`
 * e só roda quando a pessoa pede. Escolher, dizer "paciente novo" e desfazer devolvem o detalhe ATUALIZADO, já
 * mapeado, como as outras escritas do Caso. O ator NÃO vai no corpo: o back o lê da sessão.
 */

import { queryOptions } from '@tanstack/react-query';
import type {
  CaseDetail,
  IwCandidatesResponse,
  IwPatientAdmissionsResponse,
  LinkIwPatientRequest,
} from '@anery/api-contracts';

import { requisitar } from '../../../shared/api/clienteHttp';
import { chavesDeCasos } from './casos';
import { mapearCasoDetalhado } from './mapeadores';
import type { CasoDetalhado } from './tipos';

/** O modo da busca que trouxe os candidatos, no vocabulário do fio. */
export type ModoDaBuscaNoIw = 'MemberCard' | 'TaxId' | 'NameAndBirthDate' | 'Name';

/** `Desconhecido`: o back mandou um modo que esta tela ainda não conhece. É diferente de `null` (o Caso não tem chave). */
export type ModoLido = ModoDaBuscaNoIw | 'Desconhecido';

export const ROTULOS_DO_MODO_DA_BUSCA: Record<ModoLido, string> = {
  MemberCard: 'pela carteira',
  TaxId: 'pelo CPF',
  NameAndBirthDate: 'pelo nome com a data de nascimento',
  Name: 'só pelo nome (o pedido não tem data de nascimento)',
  Desconhecido: 'por um critério que esta tela ainda não conhece',
};

/** Um paciente do IW candidato ao paciente do Caso. Vive só na tela: nunca é gravado. */
export interface CandidatoDoIw {
  readonly idPaciente: string;
  readonly nome: string;
  /** AAAA-MM-DD, como o IW manda; `null` = o IW não tem. */
  readonly nascimento: string | null;
  readonly cpfMascarado: string | null;
  readonly carteira: string | null;
  readonly operadora: string | null;
  readonly admissaoAtiva: boolean;
}

export interface CandidatosDoIw {
  /** `null` = o Caso não tem carteira, CPF confirmado nem nome para buscar. */
  readonly modo: ModoLido | null;
  readonly vinculadoA: string | null;
  /** Na ordem que o IW devolveu: a plataforma não ranqueia, a admissão ativa só ganha uma marca. */
  readonly candidatos: readonly CandidatoDoIw[];
}

function ehModo(valor: unknown): valor is ModoDaBuscaNoIw {
  return typeof valor === 'string' && Object.hasOwn(ROTULOS_DO_MODO_DA_BUSCA, valor);
}

function modoLido(valor: string | null | undefined): ModoLido | null {
  if (valor === null || valor === undefined || valor === '') return null;
  return ehModo(valor) ? valor : 'Desconhecido';
}

export function mapearCandidatosDoIw(resposta: IwCandidatesResponse): CandidatosDoIw {
  // Sem id ou sem nome não há como escolher nem mostrar: o back já descarta, e aqui não se confia nisso. O mesmo
  // paciente duas vezes (uma linha por admissão, por exemplo) vira uma só: o id é a chave da lista.
  const vistos = new Set<string>();
  const candidatos = (resposta.candidates ?? []).flatMap((c): CandidatoDoIw[] => {
    if (!c.patientId || !c.name || vistos.has(c.patientId)) return [];
    vistos.add(c.patientId);
    return [
      {
        idPaciente: c.patientId,
        nome: c.name,
        nascimento: c.birthDate ?? null,
        cpfMascarado: c.maskedTaxId ?? null,
        carteira: c.memberCard ?? null,
        operadora: c.payer ?? null,
        admissaoAtiva: c.activeAdmission,
      },
    ];
  });
  return {
    modo: modoLido(resposta.mode),
    vinculadoA: resposta.linkedPatientId ?? null,
    candidatos,
  };
}

/** `GET /casos/{id}/vinculo-iw/candidatos`: busca no IW agora. IW fora do ar: `503 IW_UNAVAILABLE`. */
export function opcoesDosCandidatosDoIw(id: string, habilitado: boolean) {
  return queryOptions({
    queryKey: chavesDeCasos.candidatosDoIw(id),
    queryFn: ({ signal }) =>
      requisitar<IwCandidatesResponse>(`/casos/${encodeURIComponent(id)}/vinculo-iw/candidatos`, { sinal: signal }).then(
        mapearCandidatosDoIw,
      ),
    enabled: habilitado && id !== '',
    staleTime: 0,
    // Nome, CPF mascarado e carteira de quem pode nem ser o paciente do Caso: nada fica em cache depois que a tela
    // sai (o padrão guardaria 5 min, e a lista velha voltaria ao reabrir o Caso).
    gcTime: 0,
    retry: false,
  });
}

/** `POST /casos/{id}/vinculo-iw`. Id fora da busca: `422`; Caso já vinculado a outro: `409`. */
export function vincularAoIw(id: string, idPaciente: string, sinal?: AbortSignal): Promise<CasoDetalhado> {
  const corpo: LinkIwPatientRequest = { patientId: idPaciente };
  return requisitar<CaseDetail>(`/casos/${encodeURIComponent(id)}/vinculo-iw`, {
    method: 'POST',
    corpo,
    sinal,
  }).then(mapearCasoDetalhado);
}

/** `POST /casos/{id}/vinculo-iw/novo`: "nenhum, é paciente novo". Vínculo que não está pendente: `409`. */
export function marcarPacienteNovoNoIw(id: string, sinal?: AbortSignal): Promise<CasoDetalhado> {
  return requisitar<CaseDetail>(`/casos/${encodeURIComponent(id)}/vinculo-iw/novo`, { method: 'POST', sinal }).then(
    mapearCasoDetalhado,
  );
}

/** `POST /casos/{id}/vinculo-iw/desfazer`: "Trocar vínculo". Caso sem vínculo: `409`. */
export function desfazerVinculoComIw(id: string, sinal?: AbortSignal): Promise<CasoDetalhado> {
  return requisitar<CaseDetail>(`/casos/${encodeURIComponent(id)}/vinculo-iw/desfazer`, { method: 'POST', sinal }).then(
    mapearCasoDetalhado,
  );
}

/** O status de uma admissão no IW, no vocabulário do fio. `Desconhecido`: o IW mandou um código que a plataforma não conhece. */
export type StatusDaAdmissaoNoIw = 'InEvaluation' | 'InCare' | 'Discharged' | 'Rejected' | 'Cancelled' | 'Desconhecido';

const STATUS_CONHECIDOS: readonly string[] = ['InEvaluation', 'InCare', 'Discharged', 'Rejected', 'Cancelled'];

/** Uma admissão que o paciente vinculado já tem no IW. Vive só na tela. */
export interface AdmissaoDoPacienteNoIw {
  readonly id: string;
  readonly status: StatusDaAdmissaoNoIw;
  /** O status como veio do back: o nome, ou o código cru do IW quando desconhecido. */
  readonly statusCru: string;
}

/** As admissões do paciente no IW e a filial dele, quando foi este Caso que o cadastrou. */
export interface AdmissoesDoPacienteNoIw {
  /** O paciente do IW vinculado ao Caso agora; `null` sem vínculo. */
  readonly idPacienteVinculado: string | null;
  readonly admissoes: readonly AdmissaoDoPacienteNoIw[];
  /** O nome da filial (`ANERY SP`...); `null` quando o vínculo veio da busca: o IW não devolve a filial do paciente. */
  readonly filialDoPaciente: string | null;
}

export function mapearAdmissoesDoPacienteNoIw(resposta: IwPatientAdmissionsResponse): AdmissoesDoPacienteNoIw {
  const admissoes = (resposta.admissions ?? []).flatMap((a): AdmissaoDoPacienteNoIw[] =>
    a.id && a.status
      ? [
          {
            id: a.id,
            status: STATUS_CONHECIDOS.includes(a.status) ? (a.status as StatusDaAdmissaoNoIw) : 'Desconhecido',
            statusCru: a.status,
          },
        ]
      : [],
  );
  return {
    idPacienteVinculado: resposta.linkedPatientId ?? null,
    admissoes,
    filialDoPaciente: resposta.patientBranch ?? null,
  };
}

/**
 * `GET /casos/{id}/vinculo-iw/admissoes`: o que o paciente vinculado já tem de admissão no IW, lido agora em nome de
 * quem está na tela. Serve para avisar ANTES do "Abrir admissão no IW"; quem decide de verdade é a fila, no envio.
 */
export function opcoesDasAdmissoesDoPacienteNoIw(id: string, habilitado: boolean) {
  return queryOptions({
    queryKey: chavesDeCasos.admissoesDoPacienteNoIw(id),
    queryFn: ({ signal }) =>
      requisitar<IwPatientAdmissionsResponse>(`/casos/${encodeURIComponent(id)}/vinculo-iw/admissoes`, {
        sinal: signal,
      }).then(mapearAdmissoesDoPacienteNoIw),
    enabled: habilitado && id !== '',
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}
